import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, InfoIcon } from 'lucide-react';
import { PageHeader } from './PageHeader';
import { CTASection } from './CTASection';
import { services } from '../data/services';
import type { Service } from '../types/service';
interface ServiceDetailProps {
  service: Service;
}
function ItemList({
  items
}: {
  items: string[];
}) {
  return <ul className="grid gap-x-8 gap-y-px sm:grid-cols-2" data-id="element-188" data-mp="h24sst9a6fz0">
      {items.map(item => <li key={item} className="flex items-start gap-3 border-b border-navy-100 py-3.5 text-[15px] text-navy-800" data-id="element-189" data-mp="c4bok5rjfw1d">
          <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold-500" aria-hidden="true" data-id="element-190" data-mp="qs6e5da91ka8" />
          {item}
        </li>)}
    </ul>;
}
export function ServiceDetail({
  service
}: ServiceDetailProps) {
  const related = services.filter(item => item.slug !== service.slug).slice(0, 3);
  return <>
      <PageHeader eyebrow="Fiduciary Services" title={service.name} description={service.intro} crumbs={[{
      label: 'Fiduciary Services',
      to: '/services'
    }, {
      label: service.name
    }]} data-id="element-191" data-mp="152mmubimxt69" />

      <section className="bg-white" data-id="element-192" data-mp="1ez92mlw3tu1f">
        <div className="mx-auto grid max-w-content gap-12 px-6 py-16 sm:py-20 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:gap-16 lg:px-8" data-id="element-193" data-mp="1jzdm8ksb74vv">
          <div className="lg:sticky lg:top-32 lg:self-start" data-id="element-194" data-mp="1o0h1p66g2ywk">
            <img src={service.image} alt={service.imageAlt} className="aspect-[4/3] w-full object-cover" loading="lazy" data-id="element-195" data-mp="1sztmlldr9vwi" />
            <div className="mt-8 border-l-2 border-gold-400 pl-5" data-id="element-196" data-mp="22pvcmipo5oe2">
              <p className="text-[15px] leading-relaxed text-navy-700" data-id="element-197" data-mp="22s7qoksb3elu">
                {service.short}
              </p>
            </div>
          </div>

          <div data-id="element-198" data-mp="2bq1o5z1t4eal">
            {service.groups ? <div className="space-y-12" data-id="element-199" data-mp="2boqejilokcbk">
                {service.groups.map(group => <div key={group.title} data-id="element-200" data-mp="cjyudnepxpn8">
                    <h2 className="font-serif text-[24px] text-navy-950" data-id="element-201" data-mp="7ni34kc82v5x">
                      {group.title}
                    </h2>
                    <p className="mt-3 text-[15px] text-navy-600" data-id="element-202" data-mp="lfxz516phsxc">{group.intro}</p>
                    <div className="mt-6" data-id="element-203" data-mp="gmlmzqj7relh">
                      <ItemList items={group.items} data-id="element-204" data-mp="gltrefpm9gv7" />
                    </div>
                  </div>)}
              </div> : <div data-id="element-205" data-mp="qcof7oiigoir">
                <h2 className="font-serif text-[24px] text-navy-950" data-id="element-206" data-mp="qd80nf7ddv1j">
                  What we handle
                </h2>
                <div className="mt-6" data-id="element-207" data-mp="14awhmbybgi1b">
                  <ItemList items={service.items ?? []} data-id="element-208" data-mp="10cp37nrbrsta" />
                </div>
              </div>}

            {service.note && <div className="mt-10 flex items-start gap-3 bg-navy-50 p-5" data-id="element-209" data-mp="366osxtrjqld1">
                <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-navy-500" aria-hidden="true" data-id="element-210" data-mp="324leqxdht2o2" />
                <p className="text-[14px] leading-relaxed text-navy-600" data-id="element-211" data-mp="2w0fe879p3obs">
                  {service.note}
                </p>
              </div>}

            <Link to="/contact" className="mt-10 inline-flex items-center gap-2 bg-navy-900 px-7 py-3.5 text-[15px] font-medium text-white transition-colors duration-150 ease-out hover:bg-navy-800" data-id="element-212" data-mp="2i90l37m2q6r0">
              Speak With Our Team
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-213" data-mp="2daz20mone7fj" />
            </Link>
          </div>
        </div>
      </section>

      <section className="border-t border-navy-100 bg-navy-50" data-id="element-214" data-mp="1zchk55lfbp5j">
        <div className="mx-auto max-w-content px-6 py-16 lg:px-8" data-id="element-215" data-mp="24aan7prfk31l">
          <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-216" data-mp="3tnw93k6m1kk8">
            <h2 className="font-serif text-[26px] text-navy-950" data-id="element-217" data-mp="3tml7nr0n0owa">
              Related Services
            </h2>
            <Link to="/services" className="inline-flex items-center gap-2 text-[14px] font-medium text-navy-700 transition-colors duration-150 ease-out hover:text-gold-600" data-id="element-218" data-mp="1yl0yobvk3oxu">
              View all services
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-219" data-mp="1olif3wjk656b" />
            </Link>
          </div>
          <div className="mt-8 grid gap-px bg-navy-100 sm:grid-cols-3" data-id="element-220" data-mp="1kkyhkrv2ozjn">
            {related.map(item => <Link key={item.slug} to={item.path} className="group flex flex-col bg-white p-6 transition-colors duration-150 ease-out hover:bg-white/60" data-id="element-221" data-mp="1fohw65f9cza9">
                <h3 className="font-serif text-[19px] leading-snug text-navy-950" data-id="element-222" data-mp="10xiqp39pyquw">
                  {item.name}
                </h3>
                <p className="mt-3 text-[14px] leading-relaxed text-navy-600" data-id="element-223" data-mp="ww6zitb75xkl">
                  {item.short}
                </p>
                <span className="mt-auto inline-flex items-center gap-2 pt-6 text-[13px] font-medium text-navy-700" data-id="element-224" data-mp="rxcgbq9bi6zy">
                  Learn More
                  <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-200 ease-out group-hover:translate-x-1" aria-hidden="true" data-id="element-225" data-mp="38j0q8bz20b42" />
                </span>
              </Link>)}
          </div>
        </div>
      </section>

      <CTASection data-id="element-226" data-mp="2ku81p0krh5jh" />
    </>;
}